import { isEmpty } from 'lodash';
import {
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from 'react-admin';
import { getClientApi } from 'src/data-communication';
import {
  GetAccountBalanceListResponseDto,
  GetAccountListSortByQueryParamTypeDto,
  SortDirectionTypeDto,
} from 'src/dtos';
import { mapAccountDtoToModel, mapBaseAccountDtoToModel } from 'src/mappers';
import { Account, BaseAccount, DownloadAccountAgrementResponse } from 'src/models';
import { Logger } from 'src/utils';

const clientApi = getClientApi();

export const syncAccountStatusList = async (params: UpdateManyParams): Promise<UpdateManyResult<{ id: string }>> => {
  const { ids } = params;
  await clientApi.accounts.syncStatusList(ids.map(anId => anId.toString()));

  return { data: ids.map(anId => anId.toString()) };
};

export const downloadAccountAgreement = async (
  params: GetOneParams,
): Promise<GetOneResult<DownloadAccountAgrementResponse>> => {
  const { id, meta } = params;

  const response = await clientApi.accounts.downloadAgreement(id);

  const fileUrl = URL.createObjectURL(response);

  return {
    data: { id, file: { url: fileUrl, type: meta.type, name: meta.name } },
  };
};

export const getAccount = async (params: GetOneParams): Promise<GetOneResult<Account>> => {
  const { id } = params;

  const response = await clientApi.accounts.retrieve({ params: { id } });

  return {
    data: mapAccountDtoToModel(response),
  };
};

const mapSortByToDto = (sortBy: string): GetAccountListSortByQueryParamTypeDto => {
  if (sortBy === 'primaryAccountHolder.firstName') {
    return GetAccountListSortByQueryParamTypeDto.FirstName;
  }

  if (sortBy === 'primaryAccountHolder.lastName') {
    return GetAccountListSortByQueryParamTypeDto.LastName;
  }

  if (sortBy === 'primaryAccountHolder.email') {
    return GetAccountListSortByQueryParamTypeDto.Email;
  }

  if (sortBy === 'type.label') {
    return GetAccountListSortByQueryParamTypeDto.Type;
  }

  return sortBy as GetAccountListSortByQueryParamTypeDto;
};

export const getAccountList = async (params: GetListParams): Promise<GetListResult<BaseAccount>> => {
  const { pagination, sort, filter } = params;
  const queryParams = {
    skip: pagination.perPage && pagination.perPage * (pagination.page - 1),
    take: pagination.perPage,
    sortBy: mapSortByToDto(sort.field),
    sortDirection: sort.order?.toLowerCase() as SortDirectionTypeDto,
    type: filter?.type,
    accountId: filter?.accountId,
    accountNumber: filter?.accountNumber,
    status: filter?.status,
    firstName: filter?.firstName,
    lastName: filter?.lastName,
    email: filter?.email,
    phoneNumber: filter?.phoneNumber,
    exclude: filter?.exclude?.join(','),
  };

  const { data, total } = await clientApi.accounts.list({ queryParams });

  let balances: GetAccountBalanceListResponseDto = { data: [], total: 0 };

  try {
    const accountIds = data.reduce<number[]>((ids, a) => {
      if (a.accountId) {
        ids.push(a.accountId);
      }

      return ids;
    }, []);

    if (!isEmpty(accountIds) && !filter.skipBalance) {
      balances = await getClientApi().balance.list({ body: { accountIds } });
    }
  } catch (error) {
    Logger.error('Error occurred while getting account balance list');
    Logger.error(error);
  }

  return {
    data: data.map(aDto =>
      mapBaseAccountDtoToModel(
        aDto,
        balances.data.find(aBalance => aBalance.accountId === aDto.accountId),
      ),
    ),
    total,
  };
};

export const linkAccount = async (params: UpdateParams): Promise<UpdateResult<Account>> => {
  const { id, data } = params;

  const response = await clientApi.accounts.link({ params: { id: id.toString() }, body: data as any });

  return {
    data: mapAccountDtoToModel(response),
  };
};

export const unlinkAccount = async (params: UpdateParams): Promise<UpdateResult<Account>> => {
  const { id, data } = params;

  const response = await clientApi.accounts.unlink({ params: { id: id.toString() }, body: data as any });

  return {
    data: mapAccountDtoToModel(response),
  };
};

export const moveAccount = async (params: UpdateParams): Promise<UpdateResult<Account>> => {
  const { id, data } = params;

  const response = await clientApi.accounts.move({ params: { id: id.toString() }, body: data as any });

  return {
    data: mapAccountDtoToModel(response),
  };
};

export const syncAccount = async (params: UpdateParams): Promise<UpdateResult<Account>> => {
  const { id, data } = params;

  const response = await clientApi.accounts.sync({ params: { id: id.toString() }, body: data });

  return {
    data: mapAccountDtoToModel(response),
  };
};

export const resetAccountAttempts = async (params: UpdateParams): Promise<UpdateResult<Account>> => {
  const { id, data } = params;

  const response = await clientApi.accounts.resetAttempts({ params: { id: id.toString() }, body: data });

  return {
    data: mapAccountDtoToModel(response),
  };
};
